import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton, Radio, RadioGroup, Typography } from "@mui/material";
import { PrimaryButton } from "../studioxr/Button";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useLoginState } from "../studioxr/auth/LoginState.ts";
import { ProfileInterface } from "ts-interfaces";
import { formatPrice } from "ui";

import { Plans } from "./useStripeStore.tsx";
import { trackEvent } from "@/libs/analyticsManager";
import { MONTHLY_PRO_CREDITS, ANNUAL_PRO_CREDITS } from "../constants/proCredits";


export const ChoosePlan = ({ onSuccess, onBack, profilePrices, setSelectedPlan }) => {
    // Pages that already let the user pick monthly/yearly (/for-sketchup*) open
    // the picker on that choice via ?billing=; everyone else starts on monthly.
    const [planValue, setPlanValue] = useState<Plans>(() =>
        new URLSearchParams(window.location.search).get("billing") === Plans.yearly ? Plans.yearly : Plans.monthly);
    const profile:ProfileInterface | any = useLoginState((state) => state.profile);
    const [nextButtonDetails, setNextButtonDetails] = useState({label:"Next", disable:false});

    const hasTrackedView = useRef(false);
    useEffect(() => {
        if (hasTrackedView.current) return;
        hasTrackedView.current = true;
        trackEvent("Subscription - Choose Plan View");
    }, []);

    // Nothing is created on Stripe until the card is submitted (#4334).
    const handleNextBtnClick = () => {
        if(!profile?.id) { window.alert('Something went wrong! you cannot proceed with the process. Please contact studio@mattoboard.com.') ; return;}
        setNextButtonDetails({label:'Processing...Please Wait',disable:true});
        setSelectedPlan(profilePrices[planValue]);
        onSuccess && onSuccess();
    };
    const calculateYearlySavings = () => {
        const yearlyPrice = profilePrices.yearly.local_price;
        const monthlyPrice = profilePrices.monthly.local_price;
        return (monthlyPrice * 12) - yearlyPrice;
    }
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPlanValue((event.target as any).value);
    };

    const renderCard = (plan: Plans, label: string, creditsLabel: string, badge?: React.ReactNode) => {
      const selected = planValue === plan;
      const labelId = `plan-${plan}-label`;
      const priceId = `plan-${plan}-price`;
      const creditsId = `plan-${plan}-credits`;
      return (
        <Box
          onClick={() => setPlanValue(plan)}
          sx={{
            border: selected ? '2px solid #1A1A1A' : '1px solid #E0E0E0',
            backgroundColor: selected ? '#F9FAFC' : 'transparent',
            borderRadius: '12px',
            padding: selected ? '15px' : '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            transition: 'border-color 120ms ease, background-color 120ms ease',
          }}
        >
          <Radio
            value={plan}
            checked={selected}
            inputProps={{
              'aria-labelledby': labelId,
              'aria-describedby': `${priceId} ${creditsId}`,
            }}
            sx={{ padding: 0, marginTop: '2px', color: '#BDBDBD', '&.Mui-checked': { color: '#000' } }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
              <Typography id={labelId} sx={{ fontWeight: 400, fontSize: '16px' }}>{label}</Typography>
              {badge}
            </Box>
            <Typography id={priceId} sx={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
              {formatPrice(profilePrices[plan].local_price, profilePrices[plan].currency, true)}
            </Typography>
            <Typography id={creditsId} sx={{ fontSize: '13px', color: '#8C8C8C' }}>
              {creditsLabel}
            </Typography>
          </Box>
        </Box>
      );
    };

    const yearlyBadge = (
      <Box sx={{
        background: '#EFE0FF',
        color: '#7A2DD8',
        fontSize: '11px',
        fontWeight: 600,
        padding: '3px 10px',
        borderRadius: '999px',
      }}>
        Best value — Save {formatPrice(calculateYearlySavings(), profilePrices.monthly.currency, false)}
      </Box>
    );

    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: "30px 25px 16px",
        width: '100%',
        maxWidth: '420px',
        marginLeft: 'auto',
        marginRight: 'auto',
        boxSizing: 'border-box',
      }}>
        <IconButton
          onClick={onBack}
          disableRipple
          sx={{
            alignSelf: 'flex-start',
            marginLeft: '-12px',
            padding: '4px 8px 4px 4px',
            color: '#8C8C8C',
            borderRadius: '6px',
            '&:hover': { backgroundColor: 'transparent', color: '#5C5C5C' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: '24px' }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'inherit' }}>Back</Typography>
        </IconButton>

        <Box sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'safe center',
          paddingBottom: '80px',
        }}>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, marginBottom: '20px' }}>
            Choose your plan
          </Typography>

          <RadioGroup
            aria-label="plan"
            name="plan"
            value={planValue}
            onChange={handleChange}
            sx={{ gap: '12px' }}
          >
            {renderCard(Plans.yearly, 'Yearly', `${ANNUAL_PRO_CREDITS} credits upfront`, yearlyBadge)}
            {renderCard(Plans.monthly, 'Monthly', `${MONTHLY_PRO_CREDITS} monthly credits`)}
          </RadioGroup>

          <PrimaryButton
            aria-label="ChoosePlan"
            fullWidth
            variant="contained"
            onClick={handleNextBtnClick}
            disabled={nextButtonDetails.disable}
            sx={{
              marginTop: '40px',
              padding: '8px',
              fontWeight: 400,
              fontSize: '18px',
              textTransform: 'capitalize',
              borderRadius: '8px',
              background: '#000',
              color: '#fff',
              '&:hover': { background: '#222' },
            }}
          >
            {nextButtonDetails.label}
          </PrimaryButton>
          <Typography sx={{ fontSize: '12px', color: '#8C8C8C', textAlign: 'center', marginTop: '12px' }}>
            Renews automatically. Cancel Anytime.
          </Typography>
        </Box>
      </Box>
    );
  };
